import { FlashList } from "@shopify/flash-list";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { FlatList, Platform } from "react-native";
import { useFragment } from "react-relay";

import { Box } from "@whoosh/design-system/src/components/primitive";

import { agendaQueryInputs_duration } from "../../../../../features/DurationBasedAgendas/atoms/agendaQueryInputs";
import {
  teeSheetGroupSizeFilterAtom,
  teeSheetHoleFilterAtom,
} from "../../../../../features/TeeSheet/atoms";
import { AuthenticationModal } from "../../../../Authentication/components/AuthenticationModal";
import { BookingWizard } from "../../../../BookingWizard/BookingWizard";
import { useLockTime } from "../../../../LockSlots/hooks/useLockTime";
import { useTimeListComponents } from "../hooks/useTimeListComponents";
import { BucketCourseMap, DurationAgendaRowData, SelectedRow } from "../types";
import { renderTimeListItem } from "../utils/renderTimeListItem";
import { TimeListFragment_agenda$key } from "./__generated__/TimeListFragment_agenda.graphql";
import { TimeListFragment_facility$key } from "./__generated__/TimeListFragment_facility.graphql";
import { TimeListFragment_session$key } from "./__generated__/TimeListFragment_session.graphql";

type Props = {
  availableTimesRef: TimeListFragment_agenda$key | null;
  sessionRef: TimeListFragment_session$key | null;
  facilityRef: TimeListFragment_facility$key | null;
  onAuthComplete?: () => void;
};

export const TimeList = ({
  availableTimesRef,
  sessionRef,
  facilityRef,
  onAuthComplete,
}: Props) => {
  const [lockTime] = useLockTime();
  const groupSize = useAtomValue(teeSheetGroupSizeFilterAtom);
  const holeCount = useAtomValue(teeSheetHoleFilterAtom);
  const duration = useAtomValue(agendaQueryInputs_duration);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SelectedRow | null>(null);
  const [shouldResumeBooking, setShouldResumeBooking] = useState(false);

  const isMobile = useResponsiveProp({
    base: 1,
    xs: 1,
    sm: 1,
    md: 1,
    agendaSplit: 0,
    lg: 0,
    xl: 0,
  });

  const session = useFragment(TimeListSessionFragmentString, sessionRef);
  const facility = useFragment(TimeListFacilityFragmentString, facilityRef);
  const agenda = useFragment(TimeListFragmentString, availableTimesRef);

  const agendaId = agenda?.id ?? "";

  const edges = facility?.courseBuckets?.edges;
  const bucketCourseMap =
    edges?.reduce((acc: BucketCourseMap, edge) => {
      const bucketId = edge?.node?.id;
      const bucketName = edge?.node?.name ?? "";
      if (bucketId) {
        const courseIds =
          edge.node.courses?.map((course) => course?.id ?? "") ?? [];
        acc[bucketId] = { name: bucketName, courseIds };
      }
      return acc;
    }, {}) ?? ({} as BucketCourseMap);
  const courseIds: string[] =
    facility?.courses?.edges
      ?.flatMap((edge) => (edge?.node?.id ? [edge.node.id] : []))
      .filter((id) => typeof id === "string") || [];
  const isLoggedIn = !!session?.user?.id;
  const hasActiveClubProfile = !!session?.currentClientProfile?.id;

  const clubSlug = facility?.club?.slug || "";

  const [bookingWizardSlotId, setBookingWizardSlotId] = useState<string | null>(
    null
  );
  const [bookingWizardDate, setBookingWizardDate] = useState<string | null>(
    null
  );

  const [bookingWizardStartTime, setBookingWizardStartTime] = useState<
    string | null
  >(null);
  const [initiallyLockedSlotIds, setInitiallyLockedSlotIds] = useState<
    string[]
  >([]);

  const dismissBookingWizard = useCallback(() => {
    setBookingWizardSlotId(null);
    setBookingWizardDate(null);
    setBookingWizardStartTime(null);
    setInitiallyLockedSlotIds([]);
  }, []);

  const handleRowPress = useCallback(
    ({ time, maxDuration, courseBucketId, loaderCallback }: SelectedRow) => {
      if (agenda) {
        lockTime(
          {
            agendaId: agenda.id,
            time,
            duration: maxDuration,
            courseBucketId: courseBucketId,
          },
          (startSlotId, slotIds) => {
            loaderCallback?.();

            setInitiallyLockedSlotIds(slotIds);
            setBookingWizardDate(agenda.date);
            setBookingWizardSlotId(startSlotId);
            setBookingWizardStartTime(time);
          }
        );
      }
    },
    [agenda, lockTime]
  );

  const onAuthModalClose = () => {
    setSelectedRow(null);
    setIsAuthModalOpen(false);
  };

  const onAuthModalComplete = useCallback(() => {
    onAuthComplete?.();
    setIsAuthModalOpen(false);
    setShouldResumeBooking(true);
  }, [onAuthComplete]);

  useEffect(() => {
    if (
      shouldResumeBooking &&
      selectedRow &&
      isLoggedIn &&
      hasActiveClubProfile
    ) {
      handleRowPress(selectedRow);
      setSelectedRow(null);
      setShouldResumeBooking(false);
    }
  }, [
    handleRowPress,
    hasActiveClubProfile,
    isLoggedIn,
    selectedRow,
    shouldResumeBooking,
  ]);

  const onRowPress = useCallback(
    (row: SelectedRow) => {
      if (isLoggedIn && hasActiveClubProfile) {
        handleRowPress(row);
      } else {
        setSelectedRow(row);
        setIsAuthModalOpen(true);
      }
    },
    [handleRowPress, hasActiveClubProfile, isLoggedIn]
  );

  const timeListData = useTimeListComponents(
    agenda ?? null,
    courseIds,
    bucketCourseMap,
    onRowPress,
    session?.effectiveClientClass === "MEMBER"
  );

  const renderItemWithProps = ({
    item,
    index,
  }: {
    item: DurationAgendaRowData;
    index: number;
  }) =>
    renderTimeListItem({
      item,
      index,
      agendaId,
    });
  const memberId = session?.currentClientProfile?.member?.id;
  if (!agenda) return null;
  return (
    <>
      {timeListData.length > 0 &&
        (Platform.OS === "web" ? (
          <FlatList
            scrollEventThrottle={20}
            bounces={false}
            data={timeListData}
            showsVerticalScrollIndicator={false}
            keyExtractor={(item, index) =>
              getItemKey(item, index, agenda?.id ?? "")
            }
            ListFooterComponent={() => <Box height={96} />}
            renderItem={renderItemWithProps}
            style={{
              paddingHorizontal: isMobile ? 0 : 32,
              paddingTop: isMobile ? 0 : 16,
            }}
          />
        ) : (
          <FlashList
            scrollEventThrottle={20}
            bounces={false}
            data={timeListData}
            showsVerticalScrollIndicator={false}
            keyExtractor={(item, index) =>
              getItemKey(item, index, agenda?.id ?? "")
            }
            ListFooterComponent={() => <Box height={96} />}
            renderItem={renderItemWithProps}
            style={{
              paddingHorizontal: isMobile ? 0 : 32,
              paddingTop: isMobile ? 0 : 16,
            }}
          />
        ))}

      <AuthenticationModal
        isOpen={isAuthModalOpen}
        onClose={onAuthModalClose}
        onComplete={onAuthModalComplete}
        clubSlug={clubSlug}
      />
      {memberId ? (
        <BookingWizard
          isOpen={!!bookingWizardSlotId && !!bookingWizardStartTime}
          onDismiss={dismissBookingWizard}
          slotId={bookingWizardSlotId}
          initiallyLockedSlotIds={initiallyLockedSlotIds}
          membershipTypeId={
            session?.currentClientProfile?.member?.membershipType?.id ?? null
          }
          currentPatronId={memberId}
          currentDate={bookingWizardDate ?? ""}
          clubSlug={session?.currentClientProfile?.member?.club?.slug ?? ""}
          slotType={"MULTI"}
          initializationOverides={{
            durationMins: duration ?? undefined,
            groupSize: groupSize ?? undefined,
            holeCount: holeCount ?? undefined,
          }}
        />
      ) : null}
    </>
  );
};

/**
 *
 * Note: I pulled this out of the above component. In general don't define functions with
 * static signatures like this in components -- since that will cause them to re-create every
 * time the component renders.
 */
const getItemKey = (
  item: DurationAgendaRowData,
  index: number,
  agendaId: string
) => {
  if (item.type === "Block") {
    return `block-${item.data.name}${item.startAt}`;
  }
  if (item.type === "Event") {
    return `event-${item.key}`;
  }
  if (item.type === "BlockingRestriction" || item.type === "InfoRestriction") {
    return `restriction-${item.data.name}`;
  }
  if (item.type === "AgendaTime") {
    return `agendatime-${agendaId}-${item.startAt}-${index}`;
  }
  if (item.type === "NoAvailableTimes") {
    return `no-available-times-${index}`;
  }
  return `error-${index}`;
};

export const TimeListSessionFragmentString = graphql`
  fragment TimeListFragment_session on Session {
    effectiveClientClass
    user {
      id
    }
    currentClientProfile {
      ... on MemberProfile {
        id
      }
      member {
        ... on Member {
          id
        }
        ... on Customer {
          id
        }
        club {
          id
          slug
          visibility
        }
        membershipType {
          id
        }
      }
    }
  }
`;

export const TimeListFacilityFragmentString = graphql`
  fragment TimeListFragment_facility on Facility {
    club {
      slug
      supportsPublic
    }
    courses {
      edges {
        node {
          id
          name
        }
      }
    }
    courseBuckets {
      edges {
        node {
          id
          name
          courses {
            id
          }
        }
      }
    }
  }
`;

export const TimeListFragmentString = graphql`
  fragment TimeListFragment_agenda on Agenda {
    id
    date
    ...useTimeListComponentsAgendaFragment_agenda
  }
`;
